import React, { useEffect, useRef, useState } from 'react';
import './AppCallModal.css';

/**
 * Parche APK cliente FiberNET (2026-10-27): llamadas de voz desde la app
 * Android del cliente hacia el panel. Recibe el offer por Socket.IO
 * ('app-incoming-call'), contesta con WebRTC (audio) y emite 'app-call-answer'.
 *
 * Uso:  <AppCallModal socket={socketRef.current} />
 */
const AppCallModal = ({ socket }) => {
  const [incoming, setIncoming] = useState(null); // {phone,name,callId,sdp}
  const [phase, setPhase] = useState('ringing'); // ringing|connecting|active|ended
  const [endedMsg, setEndedMsg] = useState('');
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const ringCtxRef = useRef(null);

  const cleanup = (stopRing = true) => {
    try { pcRef.current && pcRef.current.close(); } catch (e) {}
    pcRef.current = null;
    try { streamRef.current && streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    streamRef.current = null;
    if (stopRing) stopRingtone();
  };

  /* Timbre con WebAudio (sin assets) */
  const startRingtone = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ringCtxRef.current = ctx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 620;
      gain.gain.value = 0.0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      let on = true;
      const iv = setInterval(() => {
        on = !on;
        gain.gain.setTargetAtTime(on ? 0.18 : 0.0, ctx.currentTime, 0.05);
      }, 900);
      osc._iv = iv;
      ringCtxRef.current._osc = osc;
    } catch (e) {}
  };
  const stopRingtone = () => {
    try {
      const ctx = ringCtxRef.current;
      if (!ctx) return;
      if (ctx._osc) { clearInterval(ctx._osc._iv); ctx._osc.stop(); }
      ctx.close();
    } catch (e) {}
    ringCtxRef.current = null;
  };

  useEffect(() => {
    if (!socket) return;

    const onIncoming = (data) => {
      if (incoming || phase === 'active') return; // ya hay una llamada
      setIncoming(data);
      setPhase('ringing');
      setSeconds(0);
      startRingtone();
    };
    const onAnswered = () => { /* otro agente contestó */ };
    const onEnded = (data) => {
      if (incoming && data && data.callId && data.callId !== incoming.callId) return;
      cleanup();
      setPhase('ended');
      setEndedMsg('Llamada finalizada');
      setTimeout(() => { setIncoming(null); setPhase('ringing'); }, 1500);
    };
    const onIce = async (data) => {
      try {
        if (!pcRef.current || !incoming || !data || data.callId !== incoming.callId) return;
        if (data.candidate && data.candidate.candidate) {
          await pcRef.current.addIceCandidate(data.candidate);
        }
      } catch (e) { console.warn('ICE app-call:', e); }
    };

    socket.on('app-incoming-call', onIncoming);
    socket.on('app-call-answered', onAnswered);
    socket.on('app-call-ended', onEnded);
    socket.on('app-call-end', onEnded);
    socket.on('app-call-ice', onIce);
    return () => {
      socket.off('app-incoming-call', onIncoming);
      socket.off('app-call-answered', onAnswered);
      socket.off('app-call-ended', onEnded);
      socket.off('app-call-end', onEnded);
      socket.off('app-call-ice', onIce);
      cleanup();
    };
  }, [socket, incoming, phase]);

  useEffect(() => {
    if (phase !== 'active') return;
    const iv = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(iv);
  }, [phase]);

  const fetchIce = async () => {
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const r = await fetch(`${base}/api/app/ice`);
      const j = await r.json();
      if (j && j.iceServers) return j.iceServers;
    } catch (e) {}
    return [{ urls: 'stun:stun.l.google.com:19302' }];
  };

  const accept = async () => {
    if (!incoming) return;
    stopRingtone();
    setPhase('connecting');
    try {
      const iceServers = await fetchIce();
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          socket.emit('app-call-ice', {
            phone: incoming.phone,
            callId: incoming.callId,
            candidate: ev.candidate.toJSON ? ev.candidate.toJSON() : ev.candidate
          });
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') setPhase('active');
        if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
          cleanup(false);
          setPhase('ended');
          setEndedMsg('Conexión perdida');
        }
      };
      await pc.setRemoteDescription({ type: 'offer', sdp: incoming.sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('app-call-answer', {
        phone: incoming.phone,
        callId: incoming.callId,
        sdp: answer.sdp
      });
    } catch (e) {
      console.error('app-call accept:', e);
      cleanup(false);
      setPhase('ended');
      setEndedMsg('No se pudo conectar el audio');
    }
  };

  const reject = () => {
    if (incoming) {
      socket.emit('app-call-reject', { phone: incoming.phone, callId: incoming.callId });
    }
    cleanup();
    setIncoming(null);
  };

  const hangup = () => {
    if (incoming) {
      socket.emit('app-call-end', { phone: incoming.phone, callId: incoming.callId, reason: 'agent-hangup' });
    }
    cleanup();
    setPhase('ended');
    setEndedMsg('Llamada finalizada');
    setTimeout(() => setIncoming(null), 1200);
  };

  const toggleMute = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach(t => (t.enabled = !next));
    setMuted(next);
  };

  if (!incoming) return null;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="appcall-overlay">
      <div className="appcall-card">
        <div className={`appcall-avatar ${phase === 'active' ? 'active' : ''}`}>
          <span role="img" aria-label="app">📱</span>
        </div>
        <div className="appcall-title">{incoming.name || 'Cliente FiberNET'}</div>
        <div className="appcall-phone">{incoming.phone}</div>
        <div className="appcall-status">
          {phase === 'ringing' && (
            <span className="appcall-badge app">Llamada desde la App FiberNET</span>
          )}
          {phase === 'ringing' && <div className="appcall-sub">Llamada entrante…</div>}
          {phase === 'connecting' && <div className="appcall-sub">Conectando…</div>}
          {phase === 'active' && <div className="appcall-sub active">{fmt(seconds)}</div>}
          {phase === 'ended' && <div className="appcall-sub">{endedMsg}</div>}
        </div>

        <div className="appcall-actions">
          {phase === 'ringing' && (
            <>
              <button className="appcall-btn reject" onClick={reject} title="Rechazar">✕</button>
              <button className="appcall-btn accept" onClick={accept} title="Contestar">📞</button>
            </>
          )}
          {(phase === 'connecting' || phase === 'active') && (
            <>
              <button className={`appcall-btn mute ${muted ? 'muted' : ''}`} onClick={toggleMute} title="Silenciar">
                {muted ? '🔇' : '🎙️'}
              </button>
              <button className="appcall-btn reject" onClick={hangup} title="Colgar">✕</button>
            </>
          )}
          {phase === 'ended' && (
            <button className="appcall-btn accept" onClick={() => setIncoming(null)}>OK</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppCallModal;
